import { ErrorSharingInfo } from '@/types/errors';

/**
 * Extracts browser information from user agent string
 */
function getBrowserInfo(): string {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Chrome')) {
    return 'Chrome';
  } else if (userAgent.includes('Firefox')) {
    return 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    return 'Safari';
  } else if (userAgent.includes('Edge')) {
    return 'Edge';
  } else if (userAgent.includes('Opera')) {
    return 'Opera';
  } else {
    return 'Unknown Browser';
  }
}

/**
 * Extracts platform information from user agent string
 */
function getPlatformInfo(): string {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Windows')) {
    return 'Windows';
  } else if (userAgent.includes('Mac')) {
    return 'macOS';
  } else if (userAgent.includes('Linux')) {
    return 'Linux';
  } else if (userAgent.includes('Android')) {
    return 'Android';
  } else if (userAgent.includes('iOS')) {
    return 'iOS';
  } else {
    return 'Unknown Platform';
  }
}

/**
 * Formats the core error information that is shared between email and clipboard
 */
function formatCoreErrorContent(errorInfo: ErrorSharingInfo): string {
  const { timestamp, errorType, errorMessage, statusCode, resourceContext, pathname } = errorInfo;

  return `ERROR INFORMATION
=================
Error Type: ${errorType.replace(/_/g, ' ').toUpperCase()}
Error Message: ${errorMessage}
Status Code: ${statusCode || 'Unknown'}
Timestamp: ${timestamp}
Page Path: ${pathname}

RESOURCE CONTEXT
================
${
  Object.keys(resourceContext).length > 0
    ? Object.entries(resourceContext)
        .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}: ${value}`)
        .join('\n')
    : 'No specific resource context available'
}

TECHNICAL DETAILS
=================
Browser: ${getBrowserInfo()}
Platform: ${getPlatformInfo()}
Screen Resolution: ${screen.width}x${screen.height}
Window Size: ${window.innerWidth}x${window.innerHeight}
User Agent: ${navigator.userAgent}`;
}

/**
 * Formats error information for email sharing
 */
export function formatErrorForEmail(errorInfo: ErrorSharingInfo): {
  subject: string;
  body: string;
} {
  const { errorType, statusCode } = errorInfo;

  // Create subject line
  const subject = `[Agricarbon Error Report] ${errorType.replace(/_/g, ' ').toUpperCase()} - ${statusCode || 'Unknown Status'}`;

  // Create structured email body
  const body = `Hello Agricarbon Support Team,

I encountered an error while using the Agricarbon application. Here are the details:

${formatCoreErrorContent(errorInfo)}

ADDITIONAL INFORMATION
======================
[User can add their description of the issue below]



---
This error report was automatically generated by the Agricarbon application.`;

  return { subject, body };
}

export function formatErrorForClipboard(errorInfo: ErrorSharingInfo): string {
  return `Agricarbon Error Report
=====================

${formatCoreErrorContent(errorInfo)}

---
This error report was automatically generated by the Agricarbon application.`;
}

/**
 * Creates a mailto URL with pre-filled error information
 */
export function createErrorEmailUrl(errorInfo: ErrorSharingInfo): string {
  const { subject, body } = formatErrorForEmail(errorInfo);

  // Manually construct the mailto URL to avoid URLSearchParams encoding issues
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:info@agricarbon.co.uk?subject=${encodedSubject}&body=${encodedBody}`;
}

/**
 * Extracts resource context information from the current page path
 */
export function extractResourceContextForSharing(pathname: string): Record<string, string> {
  const context: Record<string, string> = {};

  // Extract IDs from URL patterns
  const pathSegments = pathname.split('/').filter(Boolean);

  // Look for customer ID (usually first segment after domain)
  if (pathSegments.length > 0 && pathSegments[0] !== 'login' && pathSegments[0] !== 'error') {
    const customerId = pathSegments[0];
    if (customerId) {
      context.customerId = customerId;
    }
  }

  // Look for project ID
  const projectIndex = pathSegments.findIndex((segment) => segment === 'projects');
  if (projectIndex !== -1 && pathSegments[projectIndex + 1]) {
    const projectId = pathSegments[projectIndex + 1];
    if (projectId) {
      context.projectId = projectId;
    }
  }

  // Look for farm ID
  const farmIndex = pathSegments.findIndex((segment) => segment === 'farms');
  if (farmIndex !== -1 && pathSegments[farmIndex + 1]) {
    const farmId = pathSegments[farmIndex + 1];
    if (farmId) {
      context.farmId = farmId;
    }
  }

  // Look for field ID
  const fieldIndex = pathSegments.findIndex((segment) => segment === 'fields');
  if (fieldIndex !== -1 && pathSegments[fieldIndex + 1]) {
    const fieldId = pathSegments[fieldIndex + 1];
    if (fieldId) {
      context.fieldId = fieldId;
    }
  }

  // Look for user ID
  const userIndex = pathSegments.findIndex((segment) => segment === 'users');
  if (userIndex !== -1 && pathSegments[userIndex + 1]) {
    const userId = pathSegments[userIndex + 1];
    if (userId) {
      context.userId = userId;
    }
  }

  return context;
}
